const EMPTY = "—";

export function formatDate(isoStr) {
  if (!isoStr) return EMPTY;
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function displayValue(value, emptyLabels = ["N/A", "Unassigned", EMPTY]) {
  if (!value || emptyLabels.includes(value)) return null;
  return value;
}

export function personKey(emp) {
  if (emp.user_id) return `u:${emp.user_id}`;
  if (emp.employee_id) return `e:${emp.employee_id}`;
  if (emp.email) return `mail:${String(emp.email).toLowerCase()}`;
  return `name:${String(emp.name || "").toLowerCase()}`;
}

export function roleDedupeKey(role) {
  if (role.employee_role_id) return `er:${role.employee_role_id}`;
  return [
    "r",
    role.role_id || "",
    role.department_title || "",
    role.active_from || "",
    role.role_title || "",
  ].join("|");
}

export function rolesSummary(roles, maxVisible = 2) {
  if (!Array.isArray(roles) || roles.length === 0) return null;
  const titles = [
    ...new Set(
      roles.map((r) => displayValue(r.role_title)).filter(Boolean)
    ),
  ];
  if (titles.length === 0) return null;
  if (titles.length <= maxVisible) return titles.join(", ");
  return `${titles.slice(0, maxVisible).join(", ")} +${titles.length - maxVisible}`;
}

function uniqueByKey(items, keyFn) {
  const map = new Map();
  items.forEach((item) => {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, item);
  });
  return [...map.values()];
}

function buildDeptLookup(deptList = []) {
  const lookup = {};
  deptList.forEach((dept) => {
    const name = dept.name || dept.title || "Unassigned";
    if (dept.id) lookup[dept.id] = name;
    if (dept.department_id) lookup[dept.department_id] = name;
    (dept.roles || []).forEach((r) => {
      if (r.department_id) lookup[r.department_id] = name;
    });
  });
  return lookup;
}

/**
 * Whether a role assignment is still in force.
 *
 * `active_to` does two jobs: an ordinary end date, and the soft-delete stamp that
 * `modify-employee-roles` DELETE writes as `now()` while keeping the row. Only the
 * date tells them apart, so treating *any* `active_to` as inactive hid assignments
 * that are still live — and with them the manager links hanging off those rows,
 * which went on granting access on Bookings while being invisible and unremovable
 * here.
 *
 * Mirrors the backend's `holds_role_now`, which is what actually decides coach
 * authority — Bookings used to reproduce that rule client-side in
 * `coachRoster.js`, but now reads it already applied off `/fetch-employee-hierarchy`,
 * so this is the only place left that judges currency from a raw `/fetch-employees`
 * row. An unparseable date reads as unbounded server-side, so it does here too —
 * failing to read a date must not hide a live role.
 *
 * `active_from` is deliberately not tested. A role starting next week is not yet in
 * force, but it still has to be listed and editable before it starts.
 */
export function isRoleCurrent(role, now = new Date()) {
  const roleData = role.role || role;
  if (roleData.is_deleted || role.is_deleted) return false;

  const activeTo = roleData.active_to || role.active_to;
  if (!activeTo) return true;

  const ends = new Date(activeTo);
  return Number.isNaN(ends.getTime()) || ends >= now;
}

function managerNameFromRole(role) {
  if (role.manager_name) return role.manager_name;
  const manager = role.managers?.[0];
  if (!manager) return null;
  return `${manager.first_name || ""} ${manager.last_name || ""}`.trim() || null;
}

function resolveDepartmentTitle(role, roleData, deptLookup, deptList) {
  if (role.department_title) return role.department_title;
  const departmentId =
    role.department_id ||
    roleData.department_id ||
    role.department ||
    role.departmentId;
  if (departmentId && deptLookup[departmentId]) return deptLookup[departmentId];
  const matchDept = (deptList || []).find((d) =>
    (d.roles || []).some((r) => r.id === roleData.id)
  );
  return matchDept?.name || "Unassigned";
}

function normalizeRole(role, idx, deptLookup, deptList) {
  const roleData = role.role || role;
  return {
    department_title: resolveDepartmentTitle(role, roleData, deptLookup, deptList),
    role_title: roleData.title || role.role_title || "N/A",
    manager_name: managerNameFromRole(role) || "N/A",
    is_primary: role.is_primary ?? idx === 0,
    role_id: roleData.id || role.role_id,
    employee_role_id: roleData.employee_role_id || role.employee_role_id,
    active_from: roleData.active_from || role.active_from,
    active_to: roleData.active_to || role.active_to,
  };
}

export function resolveEmployeeId(emp) {
  const id = emp?.employee_id || emp?.id || null;
  if (!id || id === emp?.user_id) return null;
  const asString = String(id);
  if (asString === "null" || asString === "undefined") return null;
  return id;
}

function formatEmployee(emp, deptLookup, deptList) {
  // `|| []` is not enough on its own: a malformed `roles` that is a non-empty
  // string is truthy and then blows up on `.filter`. One bad row must not take
  // the whole list down — the Bookings executive roster reads this too now.
  const candidate = emp.current_roles?.length ? emp.current_roles : emp.roles;
  const rawRoles = Array.isArray(candidate) ? candidate : [];
  // Wrapped rather than passed by reference: `filter` hands the callback an index
  // as its second argument, which `isRoleCurrent` reads as `now`.
  const roles = uniqueByKey(
    rawRoles
      .filter((role) => isRoleCurrent(role))
      .map((role, idx) => normalizeRole(role, idx, deptLookup, deptList)),
    roleDedupeKey
  );

  return {
    name: `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || EMPTY,
    email: emp.email || "",
    is_verified: emp.is_verified_employee,
    user_id: emp.user_id,
    employee_id: resolveEmployeeId(emp),
    joined_at: emp.created_at || null,
    current_roles: roles,
  };
}

/** API may return the same person more than once — one row, merged roles. */
export function formatEmployeeList(apiEmployees, deptList = []) {
  if (!Array.isArray(apiEmployees)) return [];

  const deptLookup = buildDeptLookup(deptList);
  const byPerson = new Map();

  apiEmployees.forEach((raw) => {
    const emp = formatEmployee(raw, deptLookup, deptList);
    const key = personKey(emp);
    const existing = byPerson.get(key);

    if (!existing) {
      byPerson.set(key, emp);
      return;
    }

    byPerson.set(key, {
      ...existing,
      is_verified: existing.is_verified || emp.is_verified,
      employee_id: existing.employee_id || emp.employee_id,
      user_id: existing.user_id || emp.user_id,
      email: existing.email || emp.email,
      name: existing.name !== EMPTY ? existing.name : emp.name,
      joined_at: existing.joined_at || emp.joined_at,
      current_roles: uniqueByKey(
        [...existing.current_roles, ...emp.current_roles],
        roleDedupeKey
      ),
    });
  });

  return [...byPerson.values()].sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" })
  );
}

export function filterEmployees(employees, searchQuery) {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return employees;

  return employees.filter((emp) => {
    if (emp.name?.toLowerCase().includes(q)) return true;
    if (emp.email?.toLowerCase().includes(q)) return true;
    return (emp.current_roles || []).some(
      (r) =>
        r.department_title?.toLowerCase().includes(q) ||
        r.role_title?.toLowerCase().includes(q) ||
        r.manager_name?.toLowerCase().includes(q)
    );
  });
}

export function getApiErrorMessage(error, fallback = "Something went wrong") {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export function getSessionEventTypes(sessionUtils) {
  return (sessionUtils?.event_types || []).flatMap(
    (grp) => grp.event_types || []
  );
}
